import throttle from 'lodash/throttle'
import lodashPick from 'lodash/pick'
import { v4 as uuidV4 } from 'uuid'
import {
  KV_LIVE_STATS_UNLEASH_FLAG, prefixTopic, LiveStatsTopics,
} from '@ahaslides-product/frontend-toolkit'
import filterProfaneWords from 'storybook-utils/profanity-filter/filterProfaneWords'
import AnswersApi from '@/api/answer'
import { getKvSyncApi } from '@/api/kvSync'
import config from '@/config'
import PresentationUtils from '@/utils/presentation'
import { isColorLight } from '@/utils/color.utils'
import { seededRandomizer } from '@/utils/random'
import store from '@/vuex/store'
import {
  AGGREGATION_LEVELS, liveStats, STATS_TYPES,
} from '@/mqtt/liveStats'
import { isFeatureEnabled } from './featureToggles.module'
import i18nPresenter from '@/i18nPresenter'

const WordCloudMutationTypes = {
  SET_WORD_MAP: 'SET_WORD_MAP',
  SET_WORDS_TO_DISPLAY: 'SET_WORDS_TO_DISPLAY',
  UPDATE_WORD_MAP: 'UPDATE_WORD_MAP',
  SET_ACTIVE_SLIDE_ID: 'SET_ACTIVE_SLIDE_ID',
  SET_ANSWER_COUNT: 'SET_ANSWER_COUNT',
}

const WORD_CLOUD_LIMIT = 500 // limit 500 words to display on the canvas. The number 500 was tested and found to be the maximum number of words that can spread fully on the canvas.

function sortTheWords(wordList) {
  return wordList.sort((a, b) => {
    // First sort by count descending
    if (a.count !== b.count) {
      return b.count - a.count
    }
    // Then sort alphabetically by originalText ascending
    if (a.originalText < b.originalText) {
      return -1
    }
    if (a.originalText > b.originalText) {
      return 1
    }
    return 0
  })
}

function _buildOriginalWordCloudData(presentation, options) {
  if (!options || !options.length) return []
  const arr = []
  options.forEach((item) => {
    if (item.title) {
      item.title = item.title.trim()
      item.title = item.title.toLowerCase()
      if (item.title.length > 25) {
        item.title = item.title.slice(0, 25)
      }

      const index = arr.findIndex((option) => {
        return option.originalText == item.title
      })

      if (index === -1) {
        const word = {
          text: presentation.filteringProfanity && item.title
            ? filterProfaneWords(item.title)
            : item.title,
          originalText: item.title,
          count: 1,
          length: item.title.length,
        }
        arr.push(word)
      } else {
        arr[index].count++
      }
    }
  })
  return sortTheWords(arr)
}

async function buildWordCloudDataFromKvSync(presentation, slide, liveStatsVersionPrefix, canSubscribeNewKafkaCluster) {
  if (!presentation || !slide.id) return []
  const statsPath = prefixTopic(
    `/${liveStatsVersionPrefix}/${config.livestatsTopicPrefix}/`,
    `${STATS_TYPES.ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
  )
  const response = await getKvSyncApi(canSubscribeNewKafkaCluster).getValuesByPath(statsPath)

  const stats = response?.body?.keysvalues
  if (!stats) return []
  const wordsList = stats.map((stat) => {
    const keyParts = stat.key.split('/')
    const word = decodeURIComponent(keyParts[keyParts.length - 1])
    return {
      text: presentation.filteringProfanity ? filterProfaneWords(word) : word,
      originalText: word,
      length: word.length,
      count: Number(stat.value),
    }
  }).filter((word) => word.count > 0)

  return sortTheWords(wordsList)
}

function _buildGroupingWordCloudData(wordCloudSmartGrouping, excludedWord = '') {
  const arr = []
  if (wordCloudSmartGrouping) {
    wordCloudSmartGrouping.forEach((item) => {
      if (item.mainDisplayingWord === excludedWord) {
        return
      }
      const word = {
        text: item.mainDisplayingWord,
        originalText: item.mainDisplayingWord,
        count: item.totalNumberCount,
        length: item.mainDisplayingWord.length,
      }
      arr.push(word)
    })
  }
  return sortTheWords(arr)
}

function addStyleToWords(arr, textColour) {
  arr = arr.sort((a, b) => {
    return b.count - a.count
  })

  if (arr.length === 0) {
    return arr
  }
  let total = 0
  const pool = 15000
  arr.forEach((item) => {
    total += item.count
  })

  arr.forEach((item, key) => {
    item.percent = (item.count / total) * 100
    item.size = Math.sqrt((pool / 100) * item.percent)

    if (item.size > 90) {
      item.size = 90
    } else if (item.size < 16) {
      item.size = 16
    }
    const colorPaletteLength = store.getters['presentationAttributeModule/presentationColorPalette'].length
    const indexColor = key % colorPaletteLength
    if (isColorLight(textColour)) {
      item.color = store.getters['presentationAttributeModule/presentationLighterColorPalette'][indexColor]
    } else {
      item.color = store.getters['presentationAttributeModule/presentationColorPalette'][indexColor]
    }
  })

  if (arr.length) {
    arr[0].isMax = true
  }
  return arr
}

const _state = {
  wordCloudSlides: {},
  slideActiveId: null,
}

const getters = {
  wordsToDisplay(state, _, rootState) {
    if (!state.slideActiveId) return []
    if (!state.wordCloudSlides[state.slideActiveId]) return []
    const { slideActive } = rootState
    return addStyleToWords(
      state.wordCloudSlides[state.slideActiveId].wordsToDisplay.slice(0, WORD_CLOUD_LIMIT),
      slideActive.textColour,
    )
  },

  wordCloudAnswerCount(state) {
    if (!state.slideActiveId) return 0
    if (!state.wordCloudSlides[state.slideActiveId]) return 0
    return state.wordCloudSlides[state.slideActiveId].answerCount
  },

  isAllowedTextRemoval() {
    const { slideActive, sharePresentation } = store.state
    const { wordCloudSmartGrouping, isGroupWordCloudWords } = slideActive
    return !sharePresentation &&
      !store.getters.isBackstage &&
      (!wordCloudSmartGrouping || !isGroupWordCloudWords)
  },

  canShowSampleWordCloud(_, __, ___, rootGetters) {
    return isFeatureEnabled(rootGetters, 'AHA-31465-word-cloud-sample')
  },

  sampleWordsToDisplay(state, _, rootState) {
    const { slideActive, presentation } = rootState
    if (!slideActive || !state.slideActiveId || presentation?.presenting) return []
    const randomize = seededRandomizer(state.slideActiveId)
    const i18nKey = 'Word cloud samples'
    const i18nValue = i18nPresenter.t(i18nKey)
    const samples = i18nValue && i18nValue !== i18nKey ? i18nValue.split(',') : []
    const sampleWords = samples.map((word) => {
      const trimmedWord = word.trim().toLowerCase()
      return {
        text: trimmedWord,
        originalText: trimmedWord,
        length: trimmedWord.length,
        count: Math.floor(randomize() * 20) + 1, // Random count between 1 and 20
      }
    }).sort((a, b) => b.count - a.count)
    // boost count of words for visual effect
    sampleWords.forEach((word, index) => {
      if (index < 1) {
        word.count += 200
      } else if (index < 3) {
        word.count += 100
      } else if (index < 4) {
        word.count += 50
      }
    })
    return addStyleToWords(
      sampleWords,
      slideActive.textColour,
    )
  },
}

function convertWordsArrayToMap(wordsArray) {
  if (!wordsArray || !wordsArray.length) return new Map()
  return wordsArray.reduce((map, item) => {
    if (!item.originalText) return map
    map.set(item.originalText, item.count)
    return map
  }, new Map())
}

const actions = {
  async initWordCloudAction({
    commit, rootState, rootGetters,
  }, slide) {
    commit(WordCloudMutationTypes.SET_ACTIVE_SLIDE_ID, slide.id)
    const { presentation } = rootState
    const { wordCloudSmartGrouping, isGroupWordCloudWords } = slide
    if (isGroupWordCloudWords && wordCloudSmartGrouping) {
      const groupedWordsOnly = _buildGroupingWordCloudData(wordCloudSmartGrouping)
      commit(WordCloudMutationTypes.SET_WORDS_TO_DISPLAY, {
        words: groupedWordsOnly,
        slideId: slide.id,
      })
      commit(WordCloudMutationTypes.SET_WORD_MAP, {
        slideId: slide.id,
        wordMap: convertWordsArrayToMap(groupedWordsOnly),
      })
    } else {
      let newOriginalWordsOnly = _buildOriginalWordCloudData(presentation, slide.SlideOptions)
      if (rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) {
        newOriginalWordsOnly = await buildWordCloudDataFromKvSync(presentation, slide, rootGetters['liveStatsV2/liveStatsVersionPrefix'], rootGetters['liveStatsV2/canSubscribeNewKafkaCluster'])
      }
      commit(WordCloudMutationTypes.SET_WORDS_TO_DISPLAY, {
        words: newOriginalWordsOnly,
        slideId: slide.id,
      })
      commit(WordCloudMutationTypes.SET_WORD_MAP, {
        slideId: slide.id,
        wordMap: convertWordsArrayToMap(newOriginalWordsOnly),
      })
    }

    commit(WordCloudMutationTypes.SET_ANSWER_COUNT, {
      slideId: slide.id,
      answerCount: slide.SlideOptions.length,
    })
  },

  prebuildWordCloudSmartGroupingAction({
    dispatch,
  }, { slide, wordCloudSmartGrouping }) {
    dispatch('updateSlideByIdAction', {
      id: slide.id,
      wordCloudSmartGrouping,
    })
  },

  updateWordsAction: throttle(async (context, { slideId }) => {
    const { presentation } = context.rootState
    const wordsMap = context.state.wordCloudSlides[slideId].wordMap
    const wordsList = Array.from(wordsMap).map(([title, count]) => ({
      text: presentation.filteringProfanity && title
        ? filterProfaneWords(title)
        : title,
      originalText: title,
      count,
      length: title.length,
    }))
    context.commit(
      WordCloudMutationTypes.SET_WORDS_TO_DISPLAY,
      {
        words: wordsList,
        slideId,
      },
    )
  }, 1000),

  listenWordCloudRealtimeUpdateAction(context, slide) {
    if (!slide.id) return
    const version = slide.version || 1
    liveStats.subscribeStats({
      statsType: STATS_TYPES.ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
      identifier: slide.id,
    }, (_, __, statsUpdate) => {
      if (statsUpdate.version !== version) return
      const count = Number(statsUpdate.answerCount)
      const word = String(statsUpdate.answerValue)
      context.commit(WordCloudMutationTypes.UPDATE_WORD_MAP, {
        word,
        count,
        slideId: statsUpdate.identifier,
      })

      context.dispatch('updateWordsAction', {
        slideId: statsUpdate.identifier,
      })

      const newSlideOptions = Array.from(count).fill({
        title: word,
      })

      context.dispatch('concatSlideOptionsAction', {
        slideId: statsUpdate.activityId,
        slideOptions: newSlideOptions,
      })
    })

    context.dispatch('liveStatsV2/fetchStatsByKeyAction', {
      statsKeys: [
        LiveStatsTopics.answerCountPerActivityTopic(slide.id, version),
      ],
    }, { root: true })

    context.dispatch('liveStatsV2/subscribeStatsAction', {
      identifier: slide.id,
      statsType: STATS_TYPES.ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
    }, { root: true })
  },

  async removeWordInCloudAction({
    rootState, rootGetters, dispatch, state,
  }, wordToBeRemoved) {
    const wordsToDisplay = getters.wordsToDisplay(state, null, rootState)
    if (!wordsToDisplay || !wordsToDisplay.length) return
    if (!getters.isAllowedTextRemoval()) return
    if (rootState.sharePresentation || rootGetters.isBackstage) return

    const { numberOfUniqueWordsInGroup, SlideOptions } = rootState.slideActive

    const newWords = SlideOptions.filter((option) => option.title != wordToBeRemoved.originalText)
    dispatch('updateSlideActiveAction', {
      SlideOptions: newWords,
      numberOfUniqueWordsInGroup: numberOfUniqueWordsInGroup - 1,
    })
    if (newWords.length === 0) {
      PresentationUtils.checkAndUpdatePresentationHasResults()
    }
    dispatch('initWordCloudAction', rootState.slideActive)
    await AnswersApi.removeWordCloudAnswer({
      slideId: state.slideActiveId,
      presentationId: rootState.presentation.id,
      text: wordToBeRemoved.originalText,
      count: wordToBeRemoved.count,
      slideVersion: rootState.slideActive.version,
    })
  },
  async createSampleWordCloudPresentationAction({ dispatch }, payload) {
    const wordCloudEntries = payload.wordCloudData.map((item) => item.title)
    const createdPresentation = await dispatch('createNewPresentationApiAction', {
      ...lodashPick(payload, ['name', 'hasDefaultSlide', 'defaultSlideConfig']),
      wordCloudEntries,
    }, { root: true })
    await AnswersApi.createWordCloudSampleAnswersViaLiveProxy({
      audience: uuidV4(),
      accessCode: createdPresentation.uniqueAccessCode,
      presentationId: createdPresentation.id,
      slideId: createdPresentation.activeSlide,
      slideVersion: 1,
      items: payload.wordCloudData,
    })
  },
}

const mutations = {
  [WordCloudMutationTypes.SET_ACTIVE_SLIDE_ID](state, slideId) {
    state.slideActiveId = slideId
  },
  [WordCloudMutationTypes.UPDATE_WORD_MAP](state, { word, count, slideId }) {
    if (!word || !slideId) return
    if (!state.wordCloudSlides[slideId]) {
      state.wordCloudSlides[slideId] = {}
    }
    if (!state.wordCloudSlides[slideId].wordMap) {
      state.wordCloudSlides[slideId].wordMap = new Map()
    }

    if (count <= 0) {
      state.wordCloudSlides[slideId].wordMap.delete(word)
    } else {
      state.wordCloudSlides[slideId].wordMap.set(word, Number(count))
    }
  },

  [WordCloudMutationTypes.SET_WORD_MAP](state, { wordMap, slideId }) {
    if (!slideId) return
    if (!state.wordCloudSlides[slideId]) {
      state.wordCloudSlides[slideId] = {}
    }
    state.wordCloudSlides = {
      ...state.wordCloudSlides,
      [slideId]: {
        ...state.wordCloudSlides[slideId],
        wordMap,
      },
    }
  },

  [WordCloudMutationTypes.SET_WORDS_TO_DISPLAY](state, { words, slideId }) {
    if (!slideId) return
    if (!state.wordCloudSlides[slideId]) {
      state.wordCloudSlides[slideId] = {}
    }
    state.wordCloudSlides = {
      ...state.wordCloudSlides,
      [slideId]: {
        ...state.wordCloudSlides[slideId],
        wordsToDisplay: JSON.parse(JSON.stringify(words)),
      },
    }
  },

  [WordCloudMutationTypes.SET_ANSWER_COUNT](state, { answerCount, slideId }) {
    if (!slideId) return
    if (!state.wordCloudSlides[slideId]) {
      state.wordCloudSlides[slideId] = {}
    }
    state.wordCloudSlides = {
      ...state.wordCloudSlides,
      [slideId]: {
        ...state.wordCloudSlides[slideId],
        answerCount,
      },
    }
  },

}

const subscribeSlideVersionPlugin = (_store) => {
  _store.subscribeAction((action, state) => {
    if (action.type === 'updateSlidesVersionAction') {
      const newActiveSlide = action.payload.newSlideVersions.find((slide) => slide.id === state.slideActive.id)
      if (!newActiveSlide) return
      store.dispatch('listenWordCloudRealtimeUpdateAction', {
        ...state.slideActive,
        version: newActiveSlide.version,
      })
    }
  })
}

const wordCloudModule = {
  state: _state,
  getters,
  actions,
  mutations,
  plugins: {
    subscribeSlideVersionPlugin,
  },
}

export default wordCloudModule
